import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { loadScreenerResults } from 'domains/signup/screener/data/localStorage';
import { Heading, Paragraph } from 'components/Typography';
import Logo from 'assets/expressable-logo.svg?react';
import {
  getQuestionTitle,
  getSortedMilestones,
  getTargetMilestoneText,
  splitQuestionAnswers,
} from 'domains/signup/screener/data/questions';
import { ScreenerGradeBadge } from 'domains/signup/screener/Components/ProgressBadge';
import { TherapyTarget } from 'domains/signup/data/types';
import MilestoneProgress from 'components/MilestoneProgress';
import config from 'config';
import { routes } from 'router/routes';

/**
 * NOTE(juan): This page is not meant to be accessed by users, instead this page is meant to be accessed by our external PDF service
 * That's why you'll see many hard-coded font values and no margins
 * More info on src\domains\signup\screener\data\pdf.ts
 */

/**
 * NOTE(juan): In order to get the headless chromium to properly pick up page break CSS rules, the parent div can't be a flex
 * (it must be a block element), this and other limitations are present on this. Be very careful with any changes as its very unstable
 */

const SIGNUP_URL = `${config.get('appBaseURL')}${routes.screener.signup}`;

export const ScreenerResultsPDF: React.FC = () => {
  const [results] = useState(loadScreenerResults());

  if (!results) {
    throw new Error('Tried to access screener PDF page but no results were found');
  }

  const { recommendEvaluation, targetPatient, questions, screenerAgeRangeTitle: screenerTitle } = results;

  const { t } = useTranslation();

  const FAQs = useMemo(() => getFAQs(t, targetPatient), [t, targetPatient]);

  return (
    <div className="w-full">
      <header className="flex justify-between items-center mb-24">
        <Heading level="h2">{t('screener.results.pdfPage.title')}</Heading>

        <Logo className="fill-current text-black-100% w-auto h-[22px]" title="Expressable" />
      </header>
      <Heading level="h6" className="mb-24">
        {t('screener.results.pdfPage.recommendation.title')}
      </Heading>
      <Paragraph className="font-semibold text-[12pt]">
        {t(`screener.results.title.evalRecommended.${recommendEvaluation}.${targetPatient}`)}
      </Paragraph>
      <Paragraph className="text-[12pt]">
        {t(`screener.results.subTitle.evalRecommended.${recommendEvaluation}.${targetPatient}`)}
      </Paragraph>

      <Paragraph className="text-[12pt]">{t(`screener.results.schedule.explanation.${targetPatient}`)}</Paragraph>

      <a href={`https://${SIGNUP_URL}`} className="text-blue-300 underline">
        {SIGNUP_URL}
      </a>
      <hr className="h-2 bg-black-200 w-full my-24" />
      <Heading level="h6" className="mb-24">
        {t('screener.results.pdfPage.answers.title')}
      </Heading>
      <div>
        {questions.map(
          ({
            question: {
              question,
              explanationTitle,
              explanationText1,
              explanationText2,
              possibleAnswers,
              targetMilestoneAnswer,
            },
            response,
            result,
          }) => {
            return (
              <div key={question} className="mb-40">
                <div className="break-inside-avoid">
                  <ScreenerGradeBadge grade={result} className="mb-24 text-[10pt]" />

                  <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
                    {question}
                  </Heading>
                  <Paragraph className="mb-16 text-[12pt]">
                    {getQuestionTitle(explanationTitle, targetPatient, response, t)}
                  </Paragraph>
                  <Paragraph className="text-[12pt]" gutterBottom={false}>
                    {explanationText1}
                  </Paragraph>
                </div>

                <div className="break-inside-avoid">
                  {targetPatient === 'child' && (
                    <MilestoneProgress
                      className="max-w-[520px] my-40"
                      milestones={getSortedMilestones(splitQuestionAnswers(possibleAnswers), targetMilestoneAnswer)}
                      currentMilestoneTitle={t('screener.results.questions.milestones.yourAnswer')}
                      currentMilestoneValue={response}
                      targetMilestoneTitle={getTargetMilestoneText(screenerTitle, t)}
                      targetMilestoneValue={targetMilestoneAnswer}
                    />
                  )}

                  <Paragraph className="text-[12pt]">
                    {result === 'support-needed'
                      ? explanationText2
                      : t(`screener.results.questions.metMilestoneSubtext.${targetPatient}`)}
                  </Paragraph>
                </div>
              </div>
            );
          },
        )}
      </div>
      <hr className="h-2 bg-black-200 w-full my-24" />

      <div>
        {FAQs.map(faq => (
          <div className="mb-24 break-inside-avoid" key={faq.title}>
            <Heading level="h6" className="mb-24">
              {faq.title}
            </Heading>
            {faq.texts.map((text, index) => (
              <Paragraph key={index} className="mb-16 text-[12pt]">
                {text}
              </Paragraph>
            ))}
          </div>
        ))}

        <a href={`https://${SIGNUP_URL}`} className="text-blue-300 underline">
          {SIGNUP_URL}
        </a>
      </div>
    </div>
  );
};

const getFAQs = (t: TFunction, target: TherapyTarget): Array<{ title: string; texts: string[] }> => [
  {
    title: t(`screener.results.nextSteps.title.${target}`),
    texts: [t(`screener.results.nextSteps.text.${target}`)],
  },
  {
    title: t(`screener.results.faq.whatHappens.title.${target}`),
    texts: [
      t(`screener.results.faq.whatHappens.text1.${target}`),
      t(`screener.results.faq.pdf.whatHappens.text2.${target}`),
    ],
  },
  {
    title: t(`screener.results.faq.whatsTheCost.title.${target}`),
    texts: [t(`screener.results.faq.whatsTheCost.text.${target}`)],
  },
  {
    title: t(`screener.results.faq.whyExpressable.title.${target}`),
    texts: [
      t(`screener.results.faq.whyExpressable.text1.${target}`),
      t(`screener.results.faq.whyExpressable.text2.${target}`),
    ],
  },
  {
    title: t(`screener.results.schedule.title`),
    texts: [t(`screener.results.schedule.explanation.${target}`)],
  },
];
